/**
 * Отметка прочтения (read receipts).
 *
 * Почему отдельный модуль: «прочитано» — это не рендер, а изменение состояния
 * НА СЕРВЕРЕ, видимое другим участникам. Такую вещь нельзя размазывать по
 * компонентам, иначе никто потом не найдёт, откуда прилетел лишний m.read.
 *
 * Главное правило, из которого всё вытекает: отмечать прочитанным можно только
 * то, что человек действительно видел. Поэтому решение «пора отметить»
 * принимает UI (окно в фокусе + лента внизу), а этот модуль отвечает лишь за
 * то, КАКОЕ событие отметить и как не отправить одно и то же дважды.
 *
 * Почему setRoomReadMarkers, а не только sendReadReceipt (проверено по коду
 * matrix-js-sdk 42.3.0, lib/client.js):
 *  - sendReadReceipt -> POST /rooms/{id}/receipt/m.read/{eventId};
 *  - setRoomReadMarkers -> POST /rooms/{id}/read_markers, и ставит ОБА маркера
 *    сразу: m.fully_read (позиция «непрочитанное ниже», её читают Element и
 *    другие клиенты) и m.read (публичная отметка, которая обнуляет счётчик).
 * Один запрос вместо двух и согласованное состояние между клиентами — поэтому
 * основной путь именно read_markers.
 */
import { NotificationCountType, type MatrixClient, type MatrixEvent, type Room } from "matrix-js-sdk";

/** Последний уже отправленный хвост: локальный echo receipt не должен запускать тот же запрос снова. */
const requestedTailByRoom = new Map<string, string>();

/**
 * Самое свежее событие комнаты, на которое допустимо ставить отметку.
 *
 * Берём событие ЛЮБОГО типа, а не только отображаемое сообщение: счётчик
 * непрочитанного считает сервер, и отметка должна стоять на реальном хвосте
 * таймлайна, иначе счётчик останется ненулевым из-за события, которое мы
 * просто не рисуем.
 *
 * Локальное эхо пропускаем: sendReadReceipt/setRoomReadMarkers на pending-событие
 * бросают «Cannot set read receipt to a pending event» — сервер такого id ещё
 * не знает.
 */
export function newestReceiptTarget(room: Room): MatrixEvent | null {
  const events = room.getLiveTimeline().getEvents();
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    const id = ev.getId();
    if (!id) continue;
    // Локальные id в matrix-js-sdk начинаются с «~» — это ещё не серверное событие.
    if (id.startsWith("~")) continue;
    if (room.hasPendingEvent(id)) continue;
    return ev;
  }
  return null;
}

/**
 * Отметить комнату прочитанной до её текущего хвоста.
 *
 * @returns event_id, на который поставлена отметка, или null если отмечать
 *          было нечего (пустая комната или всё уже прочитано).
 *
 * Ошибки НЕ глушим — вызывающий код должен показать их человеку. Молча
 * проглоченный сбой здесь выглядел бы как «счётчик почему-то не обнуляется».
 */
export async function markRoomRead(
  client: MatrixClient,
  room: Room,
): Promise<string | null> {
  const me = client.getUserId();
  if (!me) return null;

  const target = newestReceiptTarget(room);
  const eventId = target?.getId();
  if (!target || !eventId) return null;

  // Локальная отметка может уже указывать на хвост (например, последнее
  // сообщение отправили мы), а серверный notification_count всё ещё быть > 0.
  // В таком случае запрос НЕ пропускаем: иначе чат навсегда остаётся с бейджем,
  // хотя пользователь дошёл до конца. Пропускаем только когда и локальная
  // отметка, и серверный счётчик уже согласны, что читать нечего.
  const serverUnread = room.getUnreadNotificationCount(NotificationCountType.Total);
  if (room.hasUserReadEvent(me, eventId) && serverUnread === 0) {
    requestedTailByRoom.delete(room.roomId);
    return null;
  }

  // setRoomReadMarkers синхронно создаёт локальный receipt и тем самым вызывает
  // новый React-render ещё до завершения HTTP-запроса. Без этого дедупа render
  // снова отправлял тот же marker, создавал следующий receipt и уходил в цикл.
  if (requestedTailByRoom.get(room.roomId) === eventId) return null;
  requestedTailByRoom.set(room.roomId, eventId);

  try {
    await client.setRoomReadMarkers(room.roomId, eventId, target);
    return eventId;
  } catch (error) {
    if (requestedTailByRoom.get(room.roomId) === eventId) {
      requestedTailByRoom.delete(room.roomId);
    }
    throw error;
  }
}
